import React from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { closeSnackbar } from "notistack";
import {
  useGetCustomAttributesQuery,
  useCreateCustomAttributeMutation,
  useDeleteCustomAttributeMutation,
} from "@tiny/api";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";

interface AddAttributeForm {
  attributeName: string;
  dataType: string;
  defaultValue?: string;
}

export function useManageCustomAttributesDialog() {
  const queryClient = useQueryClient();
  const [dataType, setDataType] = React.useState("STRING");
  const [isDeletingId, setIsDeletingId] = React.useState<string | null>(null);

  const form = useForm<AddAttributeForm>({
    defaultValues: { attributeName: "", dataType: "STRING", defaultValue: "" },
  });

  const { data, isLoading, error } = useGetCustomAttributesQuery();
  const attributes = data?.customAttributeDefinitions ?? [];

  const { mutateAsync: createAttribute, isLoading: isAdding } =
    useCreateCustomAttributeMutation();

  const { mutateAsync: deleteAttribute } = useDeleteCustomAttributeMutation();

  const handleAdd = form.handleSubmit(async (values) => {
    try {
      const rawDefault = values.defaultValue || undefined;
      // ARRAY defaults are comma-separated in the UI; send them as an array,
      // same as the CSV upload flow.
      const defaultValue =
        dataType === "ARRAY" && rawDefault
          ? rawDefault
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : rawDefault;
      await createAttribute({
        attributeName: values.attributeName,
        dataType: dataType,
        defaultValue,
      });
      queryClient.invalidateQueries(["GET", "/custom-attributes/get"]);
      form.reset({ attributeName: "", dataType: "STRING", defaultValue: "" });
      setDataType("STRING");
      showUploadMessage({
        variant: "success",
        message: "Attribute created successfully!",
        description: "Your custom attribute has been added.",
        primaryCtaActionText: "Close",
        primaryCtaActionFunction: () => closeSnackbar(),
      });
    } catch (error: any) {
      showUploadMessage({
        variant: "failed",
        message: "Failed to create attribute.",
        description: error?.response?.data?.message || "Please try again.",
        showClose: true,
      });
    }
  });

  const handleDelete = async (id: string) => {
    setIsDeletingId(id);
    try {
      await deleteAttribute({ customAttributeId: id });
      queryClient.invalidateQueries(["GET", "/custom-attributes/get"]);
      showUploadMessage({
        variant: "success",
        message: "Attribute deleted successfully!",
        description: "The custom attribute has been removed.",
        primaryCtaActionText: "Close",
        primaryCtaActionFunction: () => closeSnackbar(),
      });
    } catch (error: any) {
      showUploadMessage({
        variant: "failed",
        message: "Failed to delete attribute.",
        description: error?.response?.data?.message || "Please try again.",
        showClose: true,
      });
    } finally {
      setIsDeletingId(null);
    }
  };

  return {
    attributes,
    isLoading,
    error,
    form,
    dataType,
    setDataType,
    handleAdd,
    handleDelete,
    isAdding,
    isDeletingId,
  };
}
